import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  List,
  Typography,
  Button,
  Empty,
  Spin,
  Space,
  Tag,
  Popconfirm,
  message,
  Select,
  Tabs
} from 'antd';
import {
  BellOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CarOutlined,
  ShoppingCartOutlined,
  CalendarOutlined,
  StarOutlined,
  MessageOutlined,
  WarningOutlined,
  SafetyCertificateOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
  CheckOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { useStores } from '../../stores';
import './NotificationsPage.scss';

const { Title, Text, Paragraph } = Typography;

const NotificationsPage = observer(() => {
  const navigate = useNavigate();
  const { notificationStore } = useStores();
  const { t, i18n } = useTranslation();
  const [filter, setFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    notificationStore.fetchNotifications();
  }, [notificationStore]);

  const getNotificationIcon = (type) => {
    const icons = {
      ORDER_CREATED: <ShoppingCartOutlined style={{ color: '#1890ff' }} />,
      ORDER_CONFIRMED: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      ORDER_SHIPPED: <CarOutlined style={{ color: '#1890ff' }} />,
      ORDER_DELIVERED: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      ORDER_CANCELLED: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
      APPOINTMENT_CREATED: <CalendarOutlined style={{ color: '#1890ff' }} />,
      APPOINTMENT_CONFIRMED: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      APPOINTMENT_CANCELLED: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
      CATEGORY_APPROVED: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      CATEGORY_REJECTED: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
      BREED_APPROVED: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      BREED_REJECTED: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
      SUBSCRIPTION_EXPIRING: <WarningOutlined style={{ color: '#fa8c16' }} />,
      NEW_REVIEW: <StarOutlined style={{ color: '#fadb14' }} />,
      REVIEW_RESPONSE: <MessageOutlined style={{ color: '#1890ff' }} />,
      KYC_VERIFIED: <SafetyCertificateOutlined style={{ color: '#52c41a' }} />,
      KYC_REJECTED: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
      SYSTEM: <InfoCircleOutlined style={{ color: '#1890ff' }} />
    };
    return icons[type] || <BellOutlined style={{ color: '#1890ff' }} />;
  };


  // Fallback URLs for different notification types
  const getActionUrl = (notification) => {
    if (notification.action_url) {
      return notification.action_url;
    }

    const fallbackUrls = {
      // Admin notifications
      'ADMIN_NEW_INQUIRY': '/admin/inquiries',
      'ADMIN_CATEGORY_SUGGESTION': '/admin/category-suggestions',
      'ADMIN_BREED_SUGGESTION': '/admin/category-suggestions',
      'ADMIN_SERVICE_PENDING': '/admin/services',
      'ADMIN_PRODUCT_PENDING': '/admin/products',
      'ADMIN_PARTNER_VERIFICATION': '/admin/partners',
      // User notifications
      'ORDER_CREATED': notification.metadata?.order_id ? `/orders/${notification.metadata.order_id}` : '/orders',
      'ORDER_CONFIRMED': notification.metadata?.order_id ? `/orders/${notification.metadata.order_id}` : '/orders',
      'ORDER_SHIPPED': notification.metadata?.order_id ? `/orders/${notification.metadata.order_id}` : '/orders',
      'ORDER_DELIVERED': notification.metadata?.order_id ? `/orders/${notification.metadata.order_id}` : '/orders',
      'ORDER_CANCELLED': notification.metadata?.order_id ? `/orders/${notification.metadata.order_id}` : '/orders',
      'APPOINTMENT_CREATED': '/my-appointments',
      'APPOINTMENT_CONFIRMED': '/my-appointments',
      'APPOINTMENT_CANCELLED': '/my-appointments',
      'CATEGORY_APPROVED': '/my-pets',
      'CATEGORY_REJECTED': '/my-pets',
      'BREED_APPROVED': '/my-pets',
      'BREED_REJECTED': '/my-pets',
      'SUBSCRIPTION_EXPIRING': '/premium',
      'NEW_REVIEW': '/partner/reviews',
      'REVIEW_RESPONSE': '/partner/reviews',
      'KYC_VERIFIED': '/partner/dashboard',
      'KYC_REJECTED': '/partner/dashboard',
    };

    return fallbackUrls[notification.notification_type] || null;
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await notificationStore.markAsRead(notification.id);
    }

    const actionUrl = getActionUrl(notification);
    if (actionUrl) {
      navigate(actionUrl);
    }
  };

  const handleDelete = async (e, notificationId) => {
    e.stopPropagation();
    const result = await notificationStore.deleteNotification(notificationId);
    if (result.success) {
      message.success(t('notifications.deleteSuccess'));
    } else {
      message.error(result.error || t('notifications.deleteFailed'));
    }
  };

  const handleMarkAllAsRead = async () => {
    const result = await notificationStore.markAllAsRead();
    if (result.success) {
      message.success(t('notifications.markAllReadSuccess'));
    } else {
      message.error(result.error || t('notifications.markAllReadFailed'));
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'el' ? 'el-GR' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getNotificationTitle = (notification) => {
    if (i18n.language === 'el' && notification.title_el) {
      return notification.title_el;
    }
    return notification.title || notification.translated_title;
  };

  const getNotificationMessage = (notification) => {
    if (i18n.language === 'el' && notification.message_el) {
      return notification.message_el;
    }
    return notification.message || notification.translated_message;
  };

  const getFilteredNotifications = () => {
    let notifications = notificationStore.notifications;

    if (activeTab === 'unread') {
      notifications = notifications.filter(n => !n.is_read);
    } else if (activeTab === 'read') {
      notifications = notifications.filter(n => n.is_read);
    }

    return notifications;
  };

  const filteredNotifications = getFilteredNotifications();

  const tabItems = [
    {
      key: 'all',
      label: t('notifications.tabs.all'),
      children: null
    },
    {
      key: 'unread',
      label: (
        <Space>
          {t('notifications.tabs.unread')}
          {notificationStore.unreadCount > 0 && (
            <Tag color="blue">{notificationStore.unreadCount}</Tag>
          )}
        </Space>
      ),
      children: null
    },
    {
      key: 'read',
      label: t('notifications.tabs.read'),
      children: null
    }
  ];

  return (
    <div className="notifications-page">
      <Card>
        <div className="notifications-header">
          <div className="title-section">
            <Title level={2}>
              <BellOutlined /> {t('notifications.pageTitle')}
            </Title>
          </div>
          <div className="actions-section">
            <Space>
              {notificationStore.unreadCount > 0 && (
                <Button
                  icon={<CheckOutlined />}
                  onClick={handleMarkAllAsRead}
                >
                  {t('notifications.markAllRead')}
                </Button>
              )}
              <Button
                icon={<SettingOutlined />}
                onClick={() => navigate('/profile/notifications')}
              >
                {t('notifications.preferencesButton')}
              </Button>
            </Space>
          </div>
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />

        {notificationStore.loading ? (
          <div className="loading-container">
            <Spin size="large" />
          </div>
        ) : filteredNotifications.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('notifications.empty')}
          />
        ) : (
          <List
            className="notifications-list"
            dataSource={filteredNotifications}
            renderItem={(notification) => (
              <List.Item
                className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                onClick={() => handleNotificationClick(notification)}
                actions={[
                  <Popconfirm
                    title={t('notifications.deleteConfirm')}
                    onConfirm={(e) => handleDelete(e, notification.id)}
                    okText={t('common.yes')}
                    cancelText={t('common.no')}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      size="small"
                    />
                  </Popconfirm>
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <div className="notification-avatar">
                      {getNotificationIcon(notification.notification_type)}
                    </div>
                  }
                  title={
                    <Text strong={!notification.is_read}>
                      {getNotificationTitle(notification)}
                    </Text>
                  }
                  description={
                    <div className="notification-description">
                      <Paragraph
                        className="notification-message"
                        ellipsis={{ rows: 2, expandable: true }}
                      >
                        {getNotificationMessage(notification)}
                      </Paragraph>
                      <Text type="secondary" className="notification-time">
                        {formatDate(notification.created_at)}
                      </Text>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Card>
    </div>
  );
});

export default NotificationsPage;
